import { Ref, RefObject } from 'react';
import { Box, Button, css, styled } from '@mui/material';
import { lighten } from '@material-ui/core';
import {
  DARK,
  GRAY_LIGHT_SECONDARY,
  GRAY_LIGHT_TERTIARY,
  WHITE
} from 'constants/colors';
import FontAwesomeSvgIcon from 'components/shared/FontAwesomeSvgIcon';
import {
  faChevronLeft,
  faChevronRight
} from '@fortawesome/pro-solid-svg-icons';
import {
  faMagnifyingGlassMinus,
  faMagnifyingGlassPlus
} from '@fortawesome/pro-light-svg-icons';
import { Interval } from '../../hooks/useTimelineInterval';
import { TIMELINE_SUMMARY_WIDTH } from '../TimelineRoute/Summary/Summary';
import { TimeWindow } from './TimeWindow';
import { TO_PLAN_DRAWER_WIDTH } from 'constants/widths';
import {
  getItemWidthInPixels,
  getNextZoomLevel,
  getPreviousZoomLevel,
  hasNextZoomLevel,
  hasPreviousZoomLevel,
  alignScrollPosition
} from '../utils';
import { differenceInSeconds } from 'date-fns';

export enum ZoomLevel {
  XXXXS = 1 / 16,
  XXXS = 1 / 9,
  XXS = 1 / 6,
  XS = 1 / 4,
  S = 1 / 2,
  M = 1,
  L = 2,
  XL = 4,
  XXL = 6,
  XXXL = 12
}

interface TimelineBarProps {
  innerRef: Ref<HTMLDivElement>;
  interval: Interval;
  zoomLevel: ZoomLevel;
  timeBarItemWidth: number;
  nextDisabled?: boolean;
  previousDisabled?: boolean;
  toPlanDrawerIsOpen?: boolean;
  scrollContainerRef?: RefObject<HTMLDivElement>;
  onChangeZoomLevel?: (level: ZoomLevel) => void;
  onChangeCurrentDay?: (day: Date) => void;
  onNext?: () => void;
  onPrevious?: () => void;
}

const TimelineBarContainer = styled(Box)`
  position: sticky;
  z-index: 40;
  top: 12px;
  display: flex;
  align-items: center;
  width: 100%;
  height: 25px;
`;

const TimeLineMaskCSS = css`
  content: '';
  position: fixed;
  z-index: 41;
  height: 40px;
  background-color: ${GRAY_LIGHT_SECONDARY};
`;

const TimelineContainer = styled(Box)`
  position: absolute;
  z-index: 42;
  top: 0;
  left: ${TIMELINE_SUMMARY_WIDTH}px;
  height: 100%;
  width: 100%;
  display: flex;
  justify-content: space-between;

  pointer-events: none;
  user-select: none;

  // Mask the overflow of the
  &:before {
    ${TimeLineMaskCSS}
    left: 0;
    width: ${TIMELINE_SUMMARY_WIDTH + 10}px;
  }

  &:after {
    ${TimeLineMaskCSS}
    right: 14px;
    width: 35px;
  }
`;

const ZoomButtonsContainer = styled(Box)`
  position: fixed;
  z-index: 43;
  display: flex;
  gap: 4px;
  margin-right: 8px;
  margin-top: 25px;
  padding: 4px;
  border-radius: 2px;
`;

const ActionButtonsContainer = styled(Box)`
  position: fixed;
  z-index: 43;
  width: calc(100% - ${TIMELINE_SUMMARY_WIDTH}px);
  margin-left: ${TIMELINE_SUMMARY_WIDTH - 40}px;
  margin-top: 25px;

  display: flex;
  justify-content: space-between;
  padding: 4px;
  border-radius: 2px;
`;

const ActionButton = styled(Button)`
  position: relative;
  z-index: 40;

  min-width: initial;
  width: 25px;
  height: 25px;
  padding: 4px;
  box-sizing: border-box;

  border: none;
  outline: none;
  border-radius: 4px;
  font-size: 12px;
  color: ${DARK};

  &:hover {
    background-color: ${WHITE};
  }

  &:disabled:not(:hover) {
    border: none;
    background: initial;
    background-color: ${GRAY_LIGHT_TERTIARY};

    svg {
      color: ${lighten(DARK, 0.5)};
    }
  }
`;

export const TimelineBar = (props: TimelineBarProps) => {
  const {
    innerRef,
    interval,
    zoomLevel,
    timeBarItemWidth,
    nextDisabled,
    previousDisabled,
    toPlanDrawerIsOpen,
    scrollContainerRef,
    onChangeZoomLevel,
    onChangeCurrentDay,
    onNext,
    onPrevious
  } = props;
  const scrollContainer = scrollContainerRef?.current;
  const containerWidth = scrollContainer?.clientWidth ?? 0;
  const hasScrollbar =
    containerWidth < timeBarItemWidth * (interval?.timeWindow?.length ?? 0);

  return (
    <TimelineBarContainer>
      {/* Gray timeline background */}
      <Box
        sx={{
          position: 'fixed',
          zIndex: 0,
          left: `${TIMELINE_SUMMARY_WIDTH - 22}px`,
          width: toPlanDrawerIsOpen
            ? `calc(100% - ${
                TIMELINE_SUMMARY_WIDTH + TO_PLAN_DRAWER_WIDTH - 4
              }px)`
            : `calc(100% - ${TIMELINE_SUMMARY_WIDTH - 4}px)`,
          height: '40px',
          marginTop: '16px',
          borderRadius: '4px',
          backgroundColor: GRAY_LIGHT_SECONDARY
        }}
      />

      <ZoomButtonsContainer>
        <ActionButton
          onClick={() => {
            const previousZoomLevel = zoomLevel;
            const newZoomLevel = getPreviousZoomLevel(zoomLevel);
            onChangeZoomLevel?.(newZoomLevel);
            alignScrollPosition(
              scrollContainer,
              newZoomLevel,
              previousZoomLevel
            );
          }}
          disabled={!hasPreviousZoomLevel(zoomLevel) || !hasScrollbar}
        >
          <FontAwesomeSvgIcon
            icon={faMagnifyingGlassMinus}
            fontSize="inherit"
          />
        </ActionButton>
        <ActionButton
          onClick={() => {
            const previousZoomLevel = zoomLevel;
            const newZoomLevel = getNextZoomLevel(zoomLevel);
            onChangeZoomLevel?.(newZoomLevel);
            alignScrollPosition(
              scrollContainer,
              newZoomLevel,
              previousZoomLevel
            );
          }}
          disabled={!hasNextZoomLevel(zoomLevel)}
        >
          <FontAwesomeSvgIcon icon={faMagnifyingGlassPlus} fontSize="inherit" />
        </ActionButton>
      </ZoomButtonsContainer>

      <ActionButtonsContainer
        sx={
          toPlanDrawerIsOpen
            ? {
                width: `calc(100% - ${
                  TIMELINE_SUMMARY_WIDTH + TO_PLAN_DRAWER_WIDTH
                }px)`
              }
            : {}
        }
      >
        <ActionButton
          onClick={() => onPrevious?.()}
          disabled={previousDisabled}
        >
          <FontAwesomeSvgIcon icon={faChevronLeft} fontSize="inherit" />
        </ActionButton>

        <ActionButton onClick={() => onNext?.()} disabled={nextDisabled}>
          <FontAwesomeSvgIcon icon={faChevronRight} fontSize="inherit" />
        </ActionButton>
      </ActionButtonsContainer>

      <TimelineContainer
        ref={innerRef}
        sx={
          toPlanDrawerIsOpen
            ? {
                '&:after': {
                  right: `${TO_PLAN_DRAWER_WIDTH + 14}px`
                }
              }
            : {}
        }
      >
        {interval?.timeWindow?.map((time, i) => {
          const previousTime = interval?.timeWindow?.[i - 1];
          const nextTime = interval?.timeWindow?.[i + 1];
          const durationInSeconds = differenceInSeconds(time, interval?.start);
          const positionX = getItemWidthInPixels(
            timeBarItemWidth,
            durationInSeconds,
            zoomLevel
          );

          return (
            <TimeWindow
              key={`${time}-${i}`}
              time={time}
              previousTime={previousTime}
              nextTime={nextTime}
              x={positionX}
              width={timeBarItemWidth}
              toPlanDrawerIsOpen={toPlanDrawerIsOpen}
              zoomLevel={zoomLevel}
              dateOffset={i == 0 ? positionX : 0}
              onChangeCurrentDay={onChangeCurrentDay}
            />
          );
        })}
      </TimelineContainer>
    </TimelineBarContainer>
  );
};
